/**
 * ImportMonthListModal — import an atwork .xlsx export into one planner month.
 *
 * Flow: pick a target month (Feb–Nov of the selected year), drop or choose
 * the .xlsx file, review the parsed preview (with ENTITY and DUPLICATE?
 * tags), then commit. Parsing happens entirely client-side with SheetJS;
 * the FIRST sheet is read, and columns are matched by NORMALISED header
 * name so both known atwork export formats (Client_Surname… and
 * [Surname]…) and column order changes are tolerated. Only a surname
 * column is required — a missing one fails with an in-modal error that
 * lists the headers actually found. Rules for header normalisation,
 * entity detection, display names, and NaN cells live in
 * utils/reviewPlanner.js (parseAtworkRows).
 *
 * Duplicates (same display name already in the planner for this year, any
 * month) are flagged in the preview but never block the import — the
 * Adviser decides.
 */

import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Upload, FileSpreadsheet } from 'lucide-react';
import {
  MONTH_NAMES,
  OPEN_MONTHS,
  parseAtworkRows,
  sortMonthClients,
  formatClientListName,
  isPastPlannerMonth,
  getCurrentSASTYearMonth,
} from '../../utils/reviewPlanner';
import { importReviewClients } from '../../api/reviewClientsApi';

export default function ImportMonthListModal({
  year,
  defaultMonth,
  existingNames, // Set of lower-cased display names already in this planner year
  onClose,
  onImported, // (importedClients, month) => void
}) {
  const [month, setMonth] = useState(
    OPEN_MONTHS.includes(defaultMonth) ? defaultMonth : OPEN_MONTHS[0]
  );
  const [parsedRows, setParsedRows] = useState(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [dropOver, setDropOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    setError('');
    setParsedRows(null);
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) {
      setError('Please choose an Excel file (.xlsx).');
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer);
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) throw new Error('The workbook contains no sheets.');
      const sheet = workbook.Sheets[firstSheetName];
      // header:1 → array-of-arrays with row 0 as the header row, so
      // parseAtworkRows can match columns by name in any order.
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });
      const parsed = parseAtworkRows(rows);
      if (parsed.length === 0) {
        throw new Error('No client rows were found in the spreadsheet.');
      }
      setParsedRows(parsed);
      setFileName(file.name);
    } catch (err) {
      setError(err.message || 'Could not read the spreadsheet.');
    }
  };

  const handleImport = async () => {
    if (!parsedRows || parsedRows.length === 0) return;
    setImporting(true);
    setError('');
    try {
      const imported = await importReviewClients(parsedRows, year, month);
      onImported(imported, month);
    } catch (err) {
      setError(err.message || 'Import failed.');
      setImporting(false);
    }
  };

  const isDuplicate = (row) =>
    existingNames.has(row.displayName.trim().toLowerCase());

  return (
    <div className="rv-overlay" onClick={onClose}>
      <div
        className="rv-modal rv-modal-wide"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Import month list"
      >
        <h3 className="rv-modal-title">
          <FileSpreadsheet size={18} /> Import month list
        </h3>

        <div className="rv-field">
          <label className="rv-label" htmlFor="rv-import-month">Target month ({year})</label>
          <select
            id="rv-import-month"
            className="rv-select"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {OPEN_MONTHS.map((m) => (
              <option key={m} value={m}>{MONTH_NAMES[m]}</option>
            ))}
          </select>
          {/* Informational only — nothing blocks and nothing auto-moves
              (permanent-anchor principle, §34.4). */}
          {isPastPlannerMonth(year, month, getCurrentSASTYearMonth()) && (
            <div className="rv-note-info">
              {MONTH_NAMES[month]} {year} has already passed; these clients will
              show as overdue until completed. For next year's list, switch the
              planner to {year + 1}.
            </div>
          )}
        </div>

        {error && <div className="rv-error">{error}</div>}

        {!parsedRows && (
          <div
            className={`rv-dropzone${dropOver ? ' rv-dropzone-over' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDropOver(true); }}
            onDragLeave={() => setDropOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDropOver(false);
              handleFile(e.dataTransfer.files?.[0]);
            }}
          >
            <Upload size={22} style={{ marginBottom: '0.4rem' }} />
            <div>Drop the atwork export here, or click to choose a .xlsx file</div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
        )}

        {parsedRows && (
          <>
            <div style={{ fontSize: '0.8rem', color: 'var(--cp-text-muted)' }}>
              <strong style={{ color: 'var(--cp-text)' }}>{parsedRows.length}</strong>{' '}
              client{parsedRows.length === 1 ? '' : 's'} parsed from {fileName}
            </div>
            <div className="rv-preview-scroll">
              <table className="rv-preview-table">
                <thead>
                  <tr>
                    <th>Display name</th>
                    <th>Source</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Preview ordering matches the month cards: individuals
                      (alphabetical by surname) then entities — the ENTITY
                      tags distinguish the groups, so no section labels. */}
                  {(({ individuals, entities }) => [...individuals, ...entities])(
                    sortMonthClients(parsedRows)
                  ).map((row, i) => (
                    <tr key={i}>
                      {/* Surname-first, exactly what the month card will
                          show after import; the stored display_name (and
                          the Source column) are unchanged. */}
                      <td style={{ fontWeight: 600 }}>{formatClientListName(row)}</td>
                      <td style={{ color: 'var(--cp-text-muted)' }}>{row.sourceString}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {row.isEntity && <span className="rv-tag rv-tag-entity">ENTITY</span>}{' '}
                        {isDuplicate(row) && (
                          <span
                            className="rv-tag rv-tag-duplicate"
                            title={`A client named "${row.displayName}" is already in the ${year} planner`}
                          >
                            DUPLICATE?
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rv-modal-actions">
              <button
                className="rv-btn rv-btn-outline"
                onClick={() => { setParsedRows(null); setFileName(''); }}
                disabled={importing}
              >
                Choose a different file
              </button>
              <button
                className="rv-btn rv-btn-primary"
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? 'Importing…' : `Import to ${MONTH_NAMES[month]}`}
              </button>
            </div>
          </>
        )}

        {!parsedRows && (
          <div className="rv-modal-actions">
            <button className="rv-btn rv-btn-outline" onClick={onClose}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
